// src/cockpit4/components/AITeamDashboard.tsx
// ============================================
// AI 团队看板（总控台 P2-功能9）- v2.0 详细改进计划
// 展示 9 个 AI 岗位的工作状态、负载率、产出量与团队整体效率
// 数据来源：RuntimeManager 执行日志聚合 + localStorage（xeaos_ai_team_activity）
// ============================================

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Brain, Megaphone, Handshake, Calculator, Workflow, BookOpen,
  FolderKanban, Settings2, Sparkles, CheckCircle2, CircleDashed,
  RefreshCw, Loader2,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

interface AIPost {
  id: string;
  name: string;
  role: string;
  icon: typeof Brain;
  color: string;
  runtime: RuntimeName | "enterprise_brain";
  description: string;
}

interface PostStatus {
  total: number;
  completed: number;
  failed: number;
  running: number;
  load: number;
}

interface TeamActivity {
  date: string;
  post: string;
  count: number;
  durationMs: number;
}

const AI_POSTS: AIPost[] = [
  { id: "ceo", name: "AI CEO", role: "战略大脑", icon: Brain, color: "#8B5CF6", runtime: RuntimeName.CEO, description: "每日晨报 / 决策建议 / 风险预警" },
  { id: "cmo", name: "AI CMO", role: "增长引擎", icon: Megaphone, color: "#F59E0B", runtime: RuntimeName.Content, description: "营销日历 / 热点追踪 / 效果诊断" },
  { id: "cso", name: "AI CSO", role: "销售团队", icon: Handshake, color: "#10B981", runtime: RuntimeName.CRM, description: "销售漏斗 / 跟进助手 / 客户360" },
  { id: "cfo", name: "AI CFO", role: "财务管家", icon: Calculator, color: "#06B6D4", runtime: RuntimeName.RevenueEngine, description: "财务三表 / 预算 / 预测" },
  { id: "coo", name: "AI COO", role: "运营中台", icon: Workflow, color: "#3B82F6", runtime: RuntimeName.Workflow, description: "工作流编排 / SOP / 效率分析" },
  { id: "cko", name: "AI CKO", role: "知识官", icon: BookOpen, color: "#EC4899", runtime: RuntimeName.Knowledge, description: "知识问答 / 推荐 / 质量评估" },
  { id: "pmo", name: "AI PMO", role: "项目官", icon: FolderKanban, color: "#F97316", runtime: RuntimeName.Enterprise, description: "项目看板 / 任务 / 风险" },
  { id: "cio", name: "AI CIO", role: "系统官", icon: Settings2, color: "#14B8A6", runtime: RuntimeName.Integration, description: "权限 / 备份 / 集成" },
  { id: "assistant", name: "AI 首席助理", role: "Hermes", icon: Sparkles, color: "#6366F1", runtime: RuntimeName.Hermes, description: "对话 / 任务执行 / 主动提醒" },
];

const ACTIVITY_KEY = "xeaos_ai_team_activity";

function loadActivity(): TeamActivity[] {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as TeamActivity[];
  } catch {
    return [];
  }
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function AITeamDashboard() {
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState<TeamActivity[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const postStatuses = useMemo(() => {
    const statusMap = new Map<string, PostStatus>();
    for (const post of AI_POSTS) {
      statusMap.set(post.id, { total: 0, completed: 0, failed: 0, running: 0, load: 0 });
    }
    try {
      if (RuntimeManager.isInitialized()) {
        const summary = RuntimeManager.getStatusSummary();
        for (const post of AI_POSTS) {
          const st = statusMap.get(post.id)!;
          const runtimeStatus = post.runtime === "enterprise_brain" ? undefined : summary.status[post.runtime];
          if (runtimeStatus === "ready") st.load = 60;
          else if (runtimeStatus === "loading") { st.load = 85; st.running = 1; }
          else if (runtimeStatus === "error") { st.load = 95; st.failed = 1; }
          else st.load = 30;
        }
      }
    } catch {
      // RuntimeManager 未初始化时使用活动记录
    }
    const today = todayStr();
    for (const act of activity) {
      if (act.date !== today) continue;
      const st = statusMap.get(act.post);
      if (!st) continue;
      st.total += act.count;
      st.completed += act.count;
      st.load = Math.min(100, st.load + act.count * 3);
    }
    return statusMap;
  }, [activity]);

  useEffect(() => {
    setActivity(loadActivity());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => setActivity(loadActivity()), 5000);
    return () => clearInterval(timer);
  }, [autoRefresh]);

  const teamStats = useMemo(() => {
    const total = AI_POSTS.reduce((s, p) => s + (postStatuses.get(p.id)?.total || 0), 0);
    const completed = AI_POSTS.reduce((s, p) => s + (postStatuses.get(p.id)?.completed || 0), 0);
    const running = AI_POSTS.reduce((s, p) => s + (postStatuses.get(p.id)?.running || 0), 0);
    const avgLoad = AI_POSTS.reduce((s, p) => s + (postStatuses.get(p.id)?.load || 0), 0) / AI_POSTS.length;
    return {
      total,
      completed,
      running,
      avgLoad: Math.round(avgLoad),
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [postStatuses]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={14} className="text-violet-400" />
          <h3 className="text-[12px] font-medium text-white">AI 团队看板</h3>
          <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">9 AI Posts</span>
        </div>
        <button
          onClick={() => setAutoRefresh((v) => !v)}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw size={10} className={autoRefresh ? "animate-spin text-emerald-400" : ""} />
          {autoRefresh ? "AUTO 5s" : "AUTO OFF"}
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-[9px] font-mono text-gray-500 uppercase">今日任务</div>
          <div className="text-lg font-semibold text-white font-mono">{teamStats.total}</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-[9px] font-mono text-gray-500 uppercase">完成率</div>
          <div className="text-lg font-semibold text-emerald-400 font-mono">{teamStats.completionRate}%</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-[9px] font-mono text-gray-500 uppercase">运行中</div>
          <div className="text-lg font-semibold text-blue-400 font-mono">{teamStats.running}</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-[9px] font-mono text-gray-500 uppercase">平均负载</div>
          <div className="text-lg font-semibold text-amber-400 font-mono">{teamStats.avgLoad}%</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-500">
          <Loader2 size={16} className="animate-spin mr-2" />
          <span className="text-[11px]">加载中...</span>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {AI_POSTS.map((post, idx) => {
            const st = postStatuses.get(post.id)!;
            const Icon = post.icon;
            const loadColor = st.load > 85 ? "#F87171" : st.load > 60 ? "#FBBF24" : "#34D399";
            return (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: idx * 0.04 }}
                className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${post.color}1a`, color: post.color }}>
                    <Icon size={14} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium text-white truncate">{post.name}</div>
                    <div className="text-[9px] font-mono text-gray-500">{post.role}</div>
                  </div>
                  {st.running > 0 && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />}
                </div>
                <div className="text-[9px] text-gray-500 leading-tight mb-2 line-clamp-2">{post.description}</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${st.load}%`, background: loadColor }} />
                  </div>
                  <span className="text-[9px] font-mono" style={{ color: loadColor }}>{st.load}%</span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-[9px] font-mono">
                  <span className="flex items-center gap-0.5 text-emerald-400">
                    <CheckCircle2 size={9} /> {st.completed}
                  </span>
                  <span className="text-gray-600">/</span>
                  <span className="text-gray-400">{st.total} 任务</span>
                  {st.failed > 0 && <span className="text-red-400 ml-auto">⚠ {st.failed}</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.04] bg-white/[0.01] text-[10px] text-gray-500">
        <CircleDashed size={11} className="text-gray-400" />
        协作链路：知识洞察 → 营销策略 → 内容生产 → 多平台分发 → 线索归集 → 财务核算，各 AI 岗位自动衔接
      </div>
    </div>
  );
}

